import { forwardRef } from "react";
import { useFormContext, useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import {
  useFieldProps,
  FieldBaseProps,
} from "@shared/features/form/useFieldProps";
import SelectBase, { OptionType } from "./SelectBase";

export type Props = FieldBaseProps & {
  name: string;
  className?: string;
  options: OptionType[];
  disabled?: boolean;
  isMulti?: boolean;
  isCreatable?: boolean;
  placeholder?: string;
  noOptionsMessage?: string;
  isLoading?: boolean;
  isWithIcon?: boolean;
  value?: OptionType | OptionType[] | null;
  onSelect?: (val: OptionType | OptionType[] | null) => void;
  onCreate?: (input: string) => void;
};

export const Select = forwardRef<any, Props>(
  (
    {
      name,
      options,
      isMulti = false,
      isCreatable = false,
      isLoading = false,
      disabled = false,
      placeholder,
      required,
      error,
      isSubmitting,
      isLoading: isFieldLoading,
      isWithIcon = false,
      className,
      value,
      onSelect,
      onCreate,
    },
    ref
  ) => {
    const form = useFormContext();
    const useRHF = !onSelect;

    const {
      field: { value: rhfValue = [], onChange: rhfOnChange, onBlur },
    } = useController({
      name,
      control: form.control,
      defaultValue: [],
      rules: {
        required: required ? "This field is required" : false,
      },
    });

    const fieldProps = useFieldProps({
      className,
      error,
      isLoading: isFieldLoading,
      isSubmitting,
      required,
    });

    const currentValue = useRHF
      ? isMulti
        ? options.filter((o) => rhfValue.includes(o.value))
        : options.find((o) => o.value === rhfValue[0]) || null
      : value ?? null;

    const handleChange = (selected: OptionType | OptionType[] | null) => {
      if (onSelect) {
        onSelect(selected);
      } else {
        if (Array.isArray(selected)) {
          rhfOnChange(selected.map((o) => o.value));
        } else if (selected) {
          rhfOnChange([selected.value]);
        } else {
          rhfOnChange([]);
        }
      }
    };

    const handleCreate = (input: string) => {
      onCreate?.(input);

      const newOption: OptionType = {
        label: input,
        value: input.toLowerCase().replace(/\s+/g, "-"),
      };

      if (onSelect) {
        if (isMulti) {
          const current = Array.isArray(value) ? value : [];
          onSelect([...current, newOption]);
        } else {
          onSelect(newOption);
        }
      } else {
        if (isMulti) {
          rhfOnChange([
            ...(Array.isArray(rhfValue) ? rhfValue : []),
            newOption.value,
          ]);
        } else {
          rhfOnChange([newOption.value]);
        }
      }
    };

    return (
      <div {...fieldProps} className={twMerge("border-none", className)}>
        <SelectBase
          ref={ref}
          name={name}
          options={options}
          isMulti={isMulti}
          isCreatable={isCreatable}
          isLoading={isLoading}
          disabled={disabled}
          placeholder={placeholder}
          isWithIcon={isWithIcon}
          value={currentValue}
          onSelect={handleChange}
          onCreate={handleCreate}
        />
      </div>
    );
  }
);

export default Select;
